// UC20-12 — SYSTEM_ADMIN: every edit of the combined medicine nomenclator, with
// the provenance of each one (wireframe pages/IstoricNomenclator.tsx). The same
// page serves the whole nomenclator and one medicine, which is why the medicine
// page serves the whole nomenclator, one position, and one code:
// /medicines/history, /medicines/:id/history and /medicines/code/:code/history.
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudLink } from '@egov-moldova/mud-react';
import { Breadcrumb, PageHeader } from '@simsm/ui-shared';
import { apiClient, loadFailureKind } from '../../app/api-client';
import { CatalogHistoryLog } from '../../features/history/CatalogHistoryLog';
import { fetchMedicineCode } from './medicines-api';

interface MedicineSummary {
  id: string;
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  volume: string;
  division: string;
}

// Ask the API for the record. This used to GET `/admin/medicines` with no id at
// all and `.find()` the id in the answer — but that route pages, its default page
// is 50 rows of the 835 in the nomenclator, and every medicine outside that page
// fell through to `null`. The subtitle of a one-medicine history then described
// the whole nomenclator instead, for about 94% of it (audit M2, spec-002 T061).
async function fetchMedicine(id: string): Promise<MedicineSummary | null> {
  try {
    const { data } = await apiClient.get<MedicineSummary>(
      `/admin/medicines/${encodeURIComponent(id)}`,
    );
    return data;
  } catch (err) {
    // A medicine that genuinely is not there has no subtitle. Anything else — a
    // 403, a database that could not answer — is rethrown rather than flattened
    // into "not found", so the screen never states an absence it did not read.
    if (loadFailureKind(err) === 'not_found') return null;
    throw err;
  }
}

export function NomenclatorHistoryPage(): JSX.Element {
  const { t } = useTranslate();
  // Two ways in, one page. `code` is how the record links here — the trail then leads
  // back to the record. `id` is the position-keyed address, kept because the history
  // this page lists is the position's whichever door you came through.
  const { id: idParam, code } = useParams();
  const navigate = useNavigate();

  const codeRow = useQuery({
    queryKey: ['admin-medicine-code', code],
    queryFn: () => fetchMedicineCode(code ?? ''),
    enabled: Boolean(code),
  });
  const id = idParam ?? codeRow.data?.medicineId;

  const medicine = useQuery({
    queryKey: ['admin-medicine-summary', id],
    queryFn: () => fetchMedicine(id ?? ''),
    enabled: Boolean(id),
  });

  const title = id ? t('history.nomenclator.entity_title') : t('history.nomenclator.title');
  // Only the whole-nomenclator route carries a lede, and it is a claim about the whole
  // nomenclator. The one-medicine route had one too — the position's identity, repeated
  // under the title — but since the breadcrumb names the position a step above, the line
  // said the same thing twice in two places a centimetre apart.
  const lede = id ? undefined : t('history.nomenclator.lede');

  return (
    <section className="simsm-page simsm-history">
      <div className="simsm-history__breadcrumb">
        <Breadcrumb
          maxVisible={4}
          label={t('common.breadcrumb.aria_label')}
          crumbs={[
            { label: t('medicines.title'), to: '/system-admin/medicines' },
            // On the one-medicine route the position gets a crumb of its own, so the
            // trail reads register › position › history rather than jumping from the
            // register straight to a page about one row of it.
            //
            // It carries no link: this history belongs to the POSITION — history.service
            // resolves a medicine id to `entity_code IN (every code of that medicine)`,
            // so what is listed is the position's own changes plus all of its codes' —
            // and a position has no page. Pointing the crumb at one of its codes would
            // name a narrower thing than the page shows.
            //
            // Until the summary arrives the crumb is left out entirely rather than
            // rendered empty: a blank step in a trail is worse than a shorter trail.
            ...(id && medicine.data
              ? [
                  {
                    label: medicine.data.dci,
                    // Links back to the record this page was opened from, when there is
                    // one. On the position-keyed address there is nothing to point at —
                    // a position has no page of its own — so the crumb stays a plain
                    // step rather than a link that guesses which of its codes to open.
                    ...(code ? { to: `/system-admin/medicines/code/${code}` } : {}),
                  },
                ]
              : []),
            { label: id ? t('history.crumb.entity') : title },
          ]}
        />
      </div>

      <PageHeader title={title} lede={lede} />

      <CatalogHistoryLog
        kinds={['MEDICINE', 'MEDICINE_CODE']}
        entityId={id}
        showEntityColumn={!id}
        entityColumnLabel={t('history.column.medicine')}
        searchPlaceholder={
          id
            ? t('history.filters.search_placeholder_entity')
            : t('history.filters.search_placeholder_medicines')
        }
      />

      <p className="simsm-history__note">
        {t('history.nomenclator.note')}{' '}
        {/* AGE's link, kept an in-app navigation: the anchor it renders would
            otherwise reload the application on the way back. */}
        <MudLink
          href="/system-admin/medicines"
          size="xs"
          onClick={(event) => {
            event.preventDefault();
            navigate('/system-admin/medicines');
          }}
        >
          {t('history.action.back_to_medicines')}
        </MudLink>
      </p>
    </section>
  );
}
